import { API, Auth } from "aws-amplify";
import {
  CreateAffiliatesRequest,
  CreatePromoCodeRequest,
  KYDAdminEvent,
  KYDEventMetrics,
  UpdateAffiliateRequest,
  UpdatePromoCodeRequest,
  KYDPerformer,
  KYDPerformerDisplay,
  KYDGuestlistDisplayState,
  KYDEventBundle,
  UpdateTicketTypeAccessCodeRequest,
  CreateAccessCodeRequest,
  KYDAdminEventSummary,
  KYDPixelSettings,
  KYDStoryAsset,
  KYDStory,
  KYDReportDay,
  KYDEventScheduledUpdateSummary,
  UpdateTicketTypeRequest,
} from "@common/interfaces/KYDEvent";
import { IKYDUser, KYDOrganizationUser } from "@common/interfaces/KYDUser";
import {
  CreateTicketTypeRequest,
  GuestlistUserStatus,
  KYDPayableFee,
} from "@common/interfaces/KYDTicket";
import { API_NAMES, getCurrentParamsUrl } from "../utils";
import { S3Response } from "react-s3-uploader";
import {
  KYDAdminFeed,
  KYDExternalLinkMetadata,
  KYDFeed,
  KYDFeedItem,
  KYDOrganizationEventLink,
  KYDOrganizationFull,
} from "@common/interfaces/KYDOrganization";
import { CognitoUser, CognitoAccessToken } from "amazon-cognito-identity-js";
import { ReportMode } from "../Manager/EventDetail/Nav/Reporting/ReportsForm";
import { KYDScope } from "@common/interfaces/ICognito";
import { ReschedulingFormValues } from "src/Manager/EventDetail/ReschedulingCampaign/schema";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import { AddFanFormValues } from "src/Manager/Campaigns/Contacts/AddFanDialog";
import {
  BodyDoc,
  KYDBlockData,
  KYDContact,
  KYDFilters,
  KYDFullContact,
} from "@common/interfaces/KYDCampaign";
import {
  KYDEngageAutomationDefinition,
} from "@common/interfaces/KYDEngageAutomation";
import { PaymentReportPeriod } from "@common/interfaces/KYDMetrics";
import { FormValues as MarketingCampaignFormValues } from "src/Manager/Campaigns/CreateCampaign/BuildCampaign";
import { DisplaySubscription } from "@common/interfaces/KYDBilling";
import Dinero from "dinero.js";
import { ADS_ACTIONS } from "./ads";

type CreateEngageAutomationPayload = {
  name: string;
  status?: "DRAFT" | "ACTIVE" | "PAUSED" | "ARCHIVED";
  trigger: Record<string, unknown>;
  steps: Array<Record<string, unknown>>;
  description?: string;
  tags?: string[];
};

type UpdateEngageAutomationPayload = Partial<CreateEngageAutomationPayload>;

const fetchQueryParams = () => {
  if (window && window.location && window.location.search) {
    const params = new URLSearchParams(window.location.search);
    const obj = Object.fromEntries(params);

    return obj;
  }
};

const admin_refreshAccessToken = async () => {
  console.log("Refresh access token");
  const currentUser = (await Auth.currentAuthenticatedUser()) as CognitoUser;
  const currentSession = await Auth.currentSession();
  const refreshToken = currentSession.getRefreshToken();

  const promise = new Promise((resolve, reject) => {
    currentUser.refreshSession(refreshToken, (err, session) => {
      console.log("Session: ", session);
      if (err) reject(err);
      else resolve(session);
    });
  });

  return promise;
};

const admin_fetchAccessToken = async (): Promise<CognitoAccessToken> => {
  const currentSession = await Auth.currentSession();
  const accessToken = currentSession.getAccessToken();

  return accessToken;
};

const admin_fetchMe = async (): Promise<KYDOrganizationUser> => {
  console.log("Fetch Me");
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN_ME, `/me`, {
      queryStringParameters,
    });

    if (response && response.updated_at) {
      const access_token = await admin_fetchAccessToken();
      if (
        access_token &&
        access_token["iat"] < new Date(response.updated_at).getTime() / 1000
      ) {
        await admin_refreshAccessToken();
      }
    }

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateMe = async (
  organization_id: string
): Promise<KYDAdminEvent> => {
  console.log("Switch Org: ", organization_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN_ME, `/me`, {
      body: { organization_id },
      queryStringParameters,
    });
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const formatError = (err: any = {}) => {
  console.log(err.response);
  const { response = {} } = err;
  const { data = {}, status } = response;
  const { response: serverResponse = {} } = data;

  return {
    statusCode: status || 500,
    message: serverResponse.message || "An unknown error occured",
  };
};

const admin_fetchEvent = async (event_id: string): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    // HERE SECTION STUFF.
    const { response } = await API.get(API_NAMES.ADMIN, `/events/${event_id}`, {
      queryStringParameters,
    });
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventSections = async (
  event_id: string,
  types: string[]
): Promise<Record<string, any>> => {
  console.log("Fetch Event Sections: ", event_id);
  try {
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/sections`,
      {
        queryStringParameters: {
          types: types.join(","),
        },
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createPromoCode = async (
  event_id: string,
  request: CreatePromoCodeRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/codes`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAccessCode = async (
  event_id: string,
  request: CreateAccessCodeRequest
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/access_codes`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAccessCode = async (
  event_id: string,
  access_code_id: string,
  request: UpdateTicketTypeAccessCodeRequest
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/access_codes/${access_code_id}`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createSystemTicketType = async (
  event_id: string,
  request: CreateTicketTypeRequest
) => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/system_ticket_types`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createTicketType = async (
  event_id: string,
  request: CreateTicketTypeRequest
) => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/ticket_types`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAffiliates = async (
  event_id: string,
  request: CreateAffiliatesRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/affiliates`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updatePromoCode = async (
  event_id: string,
  promo_code_id: string,
  request: UpdatePromoCodeRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/codes/${promo_code_id}`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAffiliate = async (
  event_id: string,
  affiliate_id: string,
  request: UpdateAffiliateRequest
): Promise<KYDAdminEvent> => {
  console.log("Fetch Event: ", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/affiliates/${affiliate_id}`,
      {
        body: request,
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateUserScopes = async (user_id: string, scopes: KYDScope[]) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/users/${user_id}`, {
      body: { scopes },
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateQRCode = async (
  qr_code_id: string,
  event_id: string
): Promise<KYDOrganizationFull> => {
  console.log("Update QR Code: ", qr_code_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/links/${qr_code_id}`,
      {
        body: {
          event_id,
        },
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createPublicUploadUrl = async (
  file: File,
  callback: (params: S3Response) => void | Promise<void | string>
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/upload/public`, {
      queryStringParameters,
      body: {
        filename: file.name,
      },
    });
    console.log("EV: ", response);
    return callback(response);
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createUploadUrl = async (
  file: File,
  callback: (params: S3Response) => void | Promise<void | string>
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/upload`, {
      queryStringParameters,
      body: {
        filename: file.name,
      },
    });
    console.log("EV: ", response);
    return callback(response);
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createEventStory = async (
  event_id: string,
  file_key: string
): Promise<{ story: KYDStory }> => {
  try {
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/story`,
      {
        body: {
          file_key,
        },
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEventStory = async (
  event_id: string,
  assets: KYDStoryAsset[]
) => {
  try {
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/story`,
      {
        body: {
          assets,
        },
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

export interface DisplayFullOrganization
  extends Omit<KYDOrganizationFull, "subscriptions"> {
  subscriptions: Array<DisplaySubscription>;
}
const admin_fetchOrganization = async (): Promise<DisplayFullOrganization> => {
  console.log("Fetch Organization");
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/organizations/me`, {
      queryStringParameters,
    });
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchBio = async (): Promise<KYDAdminFeed> => {
  console.log("Fetch Organization");
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/me/bio`,
      {
        queryStringParameters,
      }
    );
    console.log("EV: ", response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEvents = async (): Promise<{
  events: KYDAdminEventSummary[];
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/events`, {
      queryStringParameters,
    });

    if (Array.isArray(response)) {
      return {
        events: response,
      };
    }

    return {
      events: response?.events,
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchAlgoliaConfig = async ({
  indexType = AlgoliaIndexType.EVENTS,
}: {
  indexType?: AlgoliaIndexType;
} = {}): Promise<{
  events_config?: {
    app_id: string;
    api_key: string;
    index_name: string;
  };
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/algolia`, {
      queryStringParameters: {
        ...queryStringParameters,
        indexType,
      },
    });

    return {
      events_config: response?.events_config,
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchFans = async (): Promise<{
  fans: KYDContact[];
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/contacts`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_fetchOrganizationUsers = async (): Promise<{
  users: KYDOrganizationUser[];
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/users`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventBundles = async (): Promise<KYDEventBundle[]> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/bundles`, {
      queryStringParameters,
    });
    return response.bundles;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventBundle = async (
  bundle_id: string
): Promise<KYDEventBundle[]> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/bundles/${bundle_id}`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEventMetrics = async (
  event_id: string,
  request?: { start?: string; end?: string }
): Promise<KYDEventMetrics> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/metrics`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          ...(request || {}),
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateTimeline = async (
  eventId: string,
  timeline: {
    start_at: string;
    end_at: string;
    doors_at: string;
    on_sale_at: string;
    announce_at: string;
  }
) => {
  console.log(`Update Timeline: ${eventId}, ${JSON.stringify(timeline)}`);
  return admin_updateEventSync(eventId, "timeline", JSON.stringify(timeline));
};

const admin_updateLink = async (linkId: string, request: any) => {
  console.log(`Update Link: ${linkId}, ${JSON.stringify(request)}`);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/organizations/me`, {
      queryStringParameters,
      body: {
        feed_item_id: linkId,
        ...request,
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createLink = async (link: string, type: KYDFeedItem["type"]) => {
  console.log(`Create Link: ${link}, ${type}`);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/organizations/me/feed`,
      {
        queryStringParameters,
        body: {
          link,
          type,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_launchEvent = async (eventId: string) => {
  console.log(`Launch Event: ${eventId}`);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/events/${eventId}`, {
      queryStringParameters,
      body: {
        action: "launch",
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

type AdminUpdateTicketTypeRequest = {
  name?: string;
  quantity?: number | string;
  price?: string;
  on_sale_at?: string;
  off_sale_at?: string;
  start_at?: string;
  end_at?: string;
  code?: string;
  hidden?: boolean;
  description?: string;
  limit?: number | string;
  waitlist_status?: string;
  waitlist_return?: string;
  increment?: number | string;
  shift_quantity_on_expiration?: boolean;
  expires_at?: string;
  status?: string;
  sold_out_finalized?: boolean;
};

const admin_updateTicketType = async (
  eventId: string,
  ticketTypeId: string,
  request: AdminUpdateTicketTypeRequest,
  options: { last_ledger_entry_index?: number } = {}
) => {
  try {
    const last_ledger_entry_index = options.last_ledger_entry_index;
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${eventId}/ticket_types`,
      {
        queryStringParameters,
        body: {
          ticket_types: [{ id: ticketTypeId, ...(request || {}) }],
          last_ledger_entry_index,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateTicketTypes = async (
  eventId: string,
  ticket_types: UpdateTicketTypeRequest[],
  options: { last_ledger_entry_index?: number } = {}
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${eventId}/ticket_types`,
      {
        queryStringParameters,
        body: {
          ticket_types,
          last_ledger_entry_index: options.last_ledger_entry_index,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteTicketType = async (
  eventId: string,
  ticketTypeId: string
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${eventId}/ticket_types/${ticketTypeId}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateOrganization = async (action: string, request: any) => {
  const urlsForActions = {
    settings: "settings",
    rebate: "settings",
    social: "social",
    feed_image: "image",
    create_link: "links",
    update_link: "links",
  };

  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/organizations/me/bio/${urlsForActions[action]}`,
      {
        queryStringParameters,
        body: {
          action,
          request,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createUser = async (request: any) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/users`, {
      queryStringParameters,
      body: request,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteUser = async (user_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(API_NAMES.ADMIN, `/users/${user_id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createEvent = async (request: any) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/events`, {
      queryStringParameters,
      body: {
        request,
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const analytics_trackEvents = async (payload: any) => {
  try {
    const { event_id } = getCurrentParamsUrl();
    const params = fetchQueryParams();
    const res = await API.post(API_NAMES.ANALYTICS, `/`, {
      queryStringParameters: {
        ...params,
        event_id,
      },
      body: payload,
    });
    return res;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchLinkMetadata = async (
  link: string
): Promise<{
  og: KYDExternalLinkMetadata;
  metadata: KYDExternalLinkMetadata;
}> => {
  try {
    const { response } = await API.post(API_NAMES.ADMIN, `/bio/validate`, {
      body: {
        link,
      },
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_festGuestlistUser = async ({ event_id, user_id }): Promise<any> => {
  try {
    const queryStringParameters = fetchQueryParams();
    console.log({ event_id, user_id });
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/${user_id}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createGuestlistUser = async ({
  event_id,
  phone_number,
  ticket_type_id,
  guestlist_first_name,
  guestlist_last_name,
  quantity,
  id,
  mode,
  tags,
  notes,
}): Promise<{
  user_id: string;
  user_name: string;
  phone_number: string;
  quantity: number;
  id: string;
  did_reserve: boolean;
  notes: string;
  tags: string[];
  statusCode?: number;
  message?: string;
  validation_result?: {
    is_valid?: boolean;
    message?: string;
  };
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/assign`,
      {
        queryStringParameters,
        body: {
          phone_number: phone_number?.startsWith("+")
            ? phone_number
            : phone_number
            ? `+${phone_number}`
            : undefined,
          ticket_type_id,
          first_name: guestlist_first_name,
          last_name: guestlist_last_name,
          quantity: parseInt(quantity),
          id,
          mode,
          notes,
          kyd_tags: tags,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteGuestlistUser = async ({
  event_id,
  phone_number,
  ticket_type_id,
  user_id,
}: {
  event_id: string;
  phone_number: string;
  ticket_type_id: string;
  user_id?: string;
}): Promise<any> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist`,
      {
        queryStringParameters,
        body: {
          phone_number,
          ticket_type_id,
          id: user_id,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

export interface KYDUserWithGuestlist extends IKYDUser {
  guestlist: {
    assigned: number;
    ticket_type_id: string;
    status: GuestlistUserStatus;
  };
  notes: string;
  tags: string[];
}

const admin_updateGuestlistAllocation = async ({
  event_id,
  allocation,
}: {
  event_id: string;
  allocation: KYDGuestlistDisplayState;
}): Promise<any> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist`,
      {
        queryStringParameters,
        body: { allocation },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchGuestlist = async (
  event_id: string
): Promise<{
  users: KYDUserWithGuestlist[];
  allocation: KYDGuestlistDisplayState;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_searchPerformers = async (
  event_id: string,
  query: string
): Promise<{ items: KYDPerformer[] }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers/search`,
      {
        queryStringParameters,
        body: { query },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createPerformer = async (
  event_id: string,
  performer_data: {
    soundcharts_id?: string;
    image_url?: string;
    name?: string;
    profiles?: {
      platform: string;
      url: string;
    }[];
  },
  skip_add_to_event = false
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers`,
      {
        queryStringParameters,
        body: {
          ...performer_data,
          performer_id: performer_data.soundcharts_id,
          skip_add_to_event,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updatePerformers = async (
  event_id: string,
  performers: KYDPerformerDisplay[]
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers`,
      {
        queryStringParameters,
        body: {
          performer_updates: performers.map((p) => ({
            id: p.id,
            headliner: p.headliner,
            set_time: p.set_time,
          })),
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateGenres = async (
  event_id: string,
  genres: { name: string; id?: string }[]
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/genres`,
      {
        queryStringParameters,
        body: {
          genre_updates: genres.map((p) => ({
            id: p.id,
            name: p.name,
          })),
        },
      }
    );
    console.log(response);

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deletePerformer = async (
  event_id: string,
  performer_id: string
): Promise<{ id: string }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/performers/${performer_id}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_cancelEvent = async (event_id: string) => {
  console.log("Cancel event", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/cancel`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteEvent = async (event_id: string) => {
  console.log("Delete event", event_id);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/archive`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateBreakeven = async (
  event_id: string,
  breakeven_amount: number
) => {
  console.log("Update Breakeven: ", breakeven_amount);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/settings`,
      {
        queryStringParameters,
        body: { breakeven_amount },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

export type SyncUpdateKeys =
  | "ticket_type_display_order"
  | "name"
  | "subtitle"
  | "age_restriction"
  | "description"
  | "description_rich"
  | "poster"
  | "rebate"
  | "timeline"
  | "tags"
  | "venue";

const admin_updateEventSync = async (
  event_id: string,
  action: SyncUpdateKeys,
  data: any
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ADMIN, `/events/${event_id}`, {
      queryStringParameters,
      body: { action, data },
    });
    const updated_event = response?.kyd_event || response;
    console.log(updated_event);
    if (updated_event?.id) {
      return updated_event;
    }

    return {
      ...updated_event,
      id: event_id,
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchFileColumns = async (event_id: string, fileKey: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/import/sample`,
      {
        queryStringParameters,
        body: { file_key: fileKey },
      }
    );
    console.log(response);
    return response as {
      schema_fields: Array<{ is_match: boolean; column: string } | null>;
      sample: any[];
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_guestlistImportCsv = async (
  event_id: string,
  fileKey: string,
  schema_fields: string[]
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/guestlist/import`,
      {
        queryStringParameters,
        body: { file_key: fileKey, schema: schema_fields },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createReportRecipients = async ({
  event_id,
  mode,
  recipients,
  custom_days,
  report_time,
}: {
  event_id: string;
  mode: ReportMode;
  recipients: string[];
  custom_days?: KYDReportDay[];
  report_time?: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/reports`,
      {
        queryStringParameters,
        body: { mode, recipients, custom_days, report_time },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchAdsReportRecipients = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ENGAGE,
      `/organizations/me/ads-reports`,
      { queryStringParameters }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createAdsReportRecipients = async ({
  mode,
  recipients,
  custom_days,
  report_time,
}: {
  mode: ReportMode;
  recipients: string[];
  custom_days?: KYDReportDay[];
  report_time?: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ENGAGE,
      `/organizations/me/ads-reports`,
      {
        queryStringParameters,
        body: { mode, recipients, custom_days, report_time },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createEventBundle = async ({
  data,
}: {
  data: {
    title: string;
  };
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ADMIN, `/bundles`, {
      queryStringParameters,
      body: data,
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEventBundle = async ({
  bundle_id,
  data,
}: {
  bundle_id: string;
  data: {
    title: string;
    events: string[];
  };
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/bundles/${bundle_id}`,
      {
        queryStringParameters,
        body: data,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateStripeConnection = async ({ code }: { code: string }) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/organizations/stripe`,
      {
        queryStringParameters,
        body: { code },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateLinkedOrganization = async (
  event_id: string,
  data: Partial<KYDOrganizationEventLink>
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/linked_organizations/${data.organization_id}`,
      {
        queryStringParameters,
        body: { organization_id: data.organization_id, scopes: data.scopes },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_linkOrganization = async (
  event_id: string,
  data: Partial<KYDOrganizationEventLink>
) => {
  console.log("Link organization", data);
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/linked_organizations`,
      {
        queryStringParameters,
        body: {
          organization_id: data.organization_id,
          phone_number: data.phone_number,
          scopes: data.scopes,
          type: data.type,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_disableOrganizationLinkForEvent = async (
  event_id: string,
  organization_id: string
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/linked_organizations/${organization_id}`,
      {
        queryStringParameters,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEventPixels = async ({
  event_id,
  pixels,
}: {
  event_id: string;
  pixels: KYDPixelSettings[];
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/pixels`,
      {
        queryStringParameters,
        body: { marketing_pixels: pixels },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_resendReceipt = async (
  event_id: string,
  user_id: string,
  payment_id: string
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/receipt`,
      {
        queryStringParameters,
        body: { user_id, payment_id },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchDashboard = async (
  period:
    | "1d"
    | "7d"
    | "28d"
    | "3m"
    | "12m"
    | "last_year"
    | "mtd"
    | "qtd"
    | "ytd" = "1d"
) => {
  try {
    let queryStringParameters = fetchQueryParams() || {};
    queryStringParameters.period = period;

    const { response } = await API.get(API_NAMES.ADMIN, `/dashboard`, {
      queryStringParameters,
    });
    console.log(response);

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_refundTickets = async (
  event_id: string,
  data: {
    tickets: {
      ticket_type_id: string;
      id: string;
    }[];
    retain_ticket: boolean;
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/refund_tickets`,
      {
        queryStringParameters,
        body: data,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_duplicateEvent = async (
  event_id: string,
  newEventOverrides: { start_at: string }
) => {
  try {
    console.log("Duplicate event", event_id);
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/duplicate`,
      {
        queryStringParameters,
        body: newEventOverrides,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateAttendeeMetadata = async (
  event_id: string,
  data: {
    id: string;
    notes: string;
    tags: string[];
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/attendees/${data.id}`,
      {
        queryStringParameters,
        body: data,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchAttendeeDetail = async (event_id: string, attendee_id) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/attendees/${attendee_id}`,
      {
        queryStringParameters,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fillWaitlistOrders = async (
  event_id: string,
  data: {
    waitlist_ids: string[];
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/waitlists/fill`,
      {
        queryStringParameters,
        body: data,
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_sendRescheduledCampaign = async (
  event_id: string,
  data: ReschedulingFormValues
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/campaigns/${event_id}/reschedule`,
      {
        queryStringParameters,
        body: data,
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchOrganizationPayouts = async ({
  period,
  start_at,
  end_at,
}: {
  period: PaymentReportPeriod;
  start_at: string;
  end_at: string;
}) => {
  try {
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/payouts`,
      {
        queryStringParameters: {
          period,
          start_at,
          end_at,
        },
      }
    );

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchSingleOrganizationPayout = async ({
  id,
  asCSV = false,
}: {
  id: string;
  asCSV?: boolean;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/payouts/${id}`,
      {
        queryStringParameters: {
          asCSV,
        },
      }
    );
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_fetchPayoutDetailsReport = async ({
  payout_id,
}: {
  payout_id: string;
}) => {
  try {
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/payouts/${payout_id}/csv`,
      {
        queryStringParameters: {
          asCSV: true,
        },
      }
    );

    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

type TicketSalesReportType = "face_value" | "rebate";
const admin_createTicketSalesReport = async ({
  start_at,
  end_at,
  report_type,
}: {
  start_at: string;
  end_at: string;
  report_type: TicketSalesReportType;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/organizations/reports/ticket-sales`,
      {
        queryStringParameters,
        body: {
          start_at,
          end_at,
          report_type,
        },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchTicketSalesReports = async ({
  report_type,
  limit = 25,
}: {
  report_type?: TicketSalesReportType;
  limit?: number;
} = {}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/reports/ticket-sales`,
      {
        queryStringParameters: {
          ...queryStringParameters,
          report_type,
          limit,
        },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateScheduledUpdate = async (
  event_id: string,
  updates: KYDEventScheduledUpdateSummary[]
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/updates`,
      {
        body: { updates },
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createScheduledUpdate = async (
  event_id: string,
  updates: KYDEventScheduledUpdateSummary[]
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/updates`,
      {
        body: { updates },
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchCampaignsDashboard = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/campaigns`, {
      queryStringParameters,
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEngageCampaigns = async (campaign_type?: "EMAIL" | "ADS") => {
  try {
    const queryStringParameters = {
      ...fetchQueryParams(),
      ...(campaign_type ? { campaign_type } : {}),
    };
    const { response } = await API.get(API_NAMES.ENGAGE, `/campaigns/engage`, {
      queryStringParameters,
    });

    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEngageAutomations = async (): Promise<{
  automations: KYDEngageAutomationDefinition[];
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/engage/automations`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEngageAutomation = async (
  automation_id: string
): Promise<{
  automation: KYDEngageAutomationDefinition;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ENGAGE,
      `/engage/automations/${automation_id}`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createEngageAutomation = async (
  payload: CreateEngageAutomationPayload
): Promise<{
  automation: KYDEngageAutomationDefinition;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ENGAGE, `/engage/automations`, {
      queryStringParameters,
      body: payload,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateEngageAutomation = async (
  automation_id: string,
  payload: UpdateEngageAutomationPayload
): Promise<{
  automation: KYDEngageAutomationDefinition;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ENGAGE,
      `/engage/automations/${automation_id}`,
      {
        queryStringParameters,
        body: payload,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createFan = async (data: AddFanFormValues) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ENGAGE, `/contacts`, {
      queryStringParameters,
      body: data,
    });
    console.log(response);
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createSegment = async (data: {
  name: string;
  filters: KYDFilters;
  query_execution_id: string;
  type: "static" | "dynamic";
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ENGAGE, `/segments`, {
      queryStringParameters,
      body: data,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};
const admin_updateSegment = async (
  id: string,
  data: {
    name: string;
    type: "static" | "dynamic";
    filters: KYDFilters;
    query_execution_id: string;
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ENGAGE, `/segments/${id}`, {
      queryStringParameters,
      body: data,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteSegment = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(API_NAMES.ENGAGE, `/segments/${id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteContact = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(API_NAMES.ENGAGE, `/contacts/${id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_unsubscribeContact = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ENGAGE,
      `/contacts/${id}/unsubscribe`,
      {
        queryStringParameters,
        body: {},
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_duplicateSegment = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ENGAGE,
      `/segments/${id}/duplicate`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteScheduledUpdate = async (
  event_id: string,
  date: string,
  type?: "description" | "poster" | "performers" | "name"
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const body: any = { date };
    if (type) body.type = type;
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/updates/delete`,
      {
        body,
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createSegmentQuery = async ({
  filters,
}: {
  filters: KYDFilters;
}): Promise<{
  size: number;
  query_execution_id: string;
  controller: AbortController;
}> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const controller = new AbortController();
    const { response } = await API.post(API_NAMES.ENGAGE, `/segments/query`, {
      queryStringParameters,
      body: { filters },
      signal: controller.signal,
    });
    return {
      ...response,
      controller,
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchOrganizationTags = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ADMIN, `/tags`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchSegments = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/segments`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchSingleSegment = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/segments/${id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_exportSegment = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ENGAGE,
      `/segments/${id}/export`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchContact = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/contacts/${id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createMarketingCampaign = async (data: {
  name: string;
  subject: string;
  preheader: string;
  blocks: KYDBlockData[];
  send_at: string;
  template_version?: number;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ENGAGE, `/campaigns`, {
      queryStringParameters,
      body: data,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_launchMarketingCampaign = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ENGAGE,
      `/campaigns/${id}/launch`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateMarketingCampaign = async ({
  id,
  data,
}: {
  id: string;
  data: MarketingCampaignFormValues;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(API_NAMES.ENGAGE, `/campaigns/${id}`, {
      queryStringParameters,
      body: data,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchMarketingCampaign = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/campaigns/${id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateContact = async (contact: Partial<KYDFullContact>) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ENGAGE,
      `/contacts/${contact.id}`,
      {
        queryStringParameters,
        body: contact,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_contactImportCsv = async (
  file_key: string,
  {
    mapping,
    genres,
    name,
  }: { mapping: string[]; genres: string[]; name: string }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(API_NAMES.ENGAGE, `/contacts/import`, {
      queryStringParameters,
      body: { file_key, mapping, genres, name },
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_duplicateCampaign = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ENGAGE,
      `/campaigns/${id}/duplicate`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteCampaign = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(API_NAMES.ENGAGE, `/campaigns/${id}`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_cancelCampaign = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ENGAGE,
      `/campaigns/${id}/cancel`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEmailTemplates = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ENGAGE,
      `/campaigns/templates`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchEmailTemplate = async (
  template_type: string,
  version?: number
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    if (version) {
      queryStringParameters.version = version.toString();
    }

    const { response } = await API.get(
      API_NAMES.ENGAGE,
      `/campaigns/templates/${template_type}`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchContactImportJob = async (id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ENGAGE,
      `/contacts/import/${id}`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchContactImportJobs = async () => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(API_NAMES.ENGAGE, `/contacts/import`, {
      queryStringParameters,
    });
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createCampaignTest = async (
  campaign_id: string,
  data: {
    recipients?: string[];
    contact_id?: string;
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ENGAGE,
      `/campaigns/${campaign_id}/test`,
      {
        queryStringParameters,
        body: data,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createHold = async (
  event_id: string,
  data: {
    name: string;
    quantity: number;
    ticket_type_id: string;
    price: string;
    expires_at: string;
  }
) => {
  try {
    console.log("Creating hold", data);
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/holds`,
      {
        body: data,
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateHold = async (
  event_id: string,
  hold_id: string,
  data: {
    name?: string;
    quantity?: number;
    ticket_type_id?: string;
    price?: string;
    expires_at?: string;
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/holds/${hold_id}`,
      {
        body: data,
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_releaseHold = async (event_id: string, hold_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/holds/${hold_id}`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchInvoices = async (): Promise<{ invoices: any[] }> => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/organizations/invoices`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateBilling = async ({
  id,
  brand,
  last4,
  exp_month,
  exp_year,
}: {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/organizations/settings/billing`,
      {
        queryStringParameters,
        body: {
          id,
          brand,
          last4,
          exp_month,
          exp_year,
        },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createBillingSetupIntent = async ({
  payment_method_id,
  return_url,
}: {
  payment_method_id: string;
  return_url: string;
}) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/organizations/settings/billing/setup-intent`,
      {
        queryStringParameters,
        body: {
          payment_method_id,
          return_url,
        },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_calculateFinalTicketPrice = async (
  price: number,
  event_id?: string,
  fee_overrides?: { type: string; amount: number }
): Promise<{
  total_price: Dinero.Dinero;
  total_fees: Dinero.Dinero;
  ticket_fees: KYDPayableFee[];
}> => {
  try {
    if (!price || isNaN(price)) {
      throw new Error("Price is required and must be a number");
    }
    const queryStringParameters = fetchQueryParams();
    const body: {
      price: number;
      event_id?: string;
      fee_overrides?: { type: string; amount: number };
    } = { price };
    if (event_id) {
      body.event_id = event_id;
    }
    if (fee_overrides) {
      body.fee_overrides = fee_overrides;
    }
    const { response } = await API.post(API_NAMES.ADMIN, `/pricing/calculate`, {
      queryStringParameters,
      body,
    });
    return {
      total_price: Dinero({
        amount: response.total_price.amount,
        currency: response.total_price.currency,
        precision: response.total_price.precision,
      }),
      total_fees: Dinero({
        amount: response.total_fees.amount,
        currency: response.total_fees.currency,
        precision: response.total_fees.precision,
      }),
      ticket_fees: response.ticket_fees,
    };
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_createConfirmationEmail = async (
  event_id: string,
  data: {
    name: string;
    note: string;
    note_rich: BodyDoc | null;
    ticket_type_ids: string[];
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.post(
      API_NAMES.ADMIN,
      `/events/${event_id}/confirmation-emails`,
      {
        queryStringParameters,
        body: data,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_updateConfirmationEmail = async (
  event_id: string,
  id: string,
  data: {
    name: string;
    note: string;
    note_rich?: BodyDoc | null;
    ticket_type_ids: string[];
  }
) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.put(
      API_NAMES.ADMIN,
      `/events/${event_id}/confirmation-emails/${id}`,
      {
        queryStringParameters,
        body: data,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_deleteConfirmationEmail = async (event_id: string, id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.del(
      API_NAMES.ADMIN,
      `/events/${event_id}/confirmation-emails/${id}`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const admin_fetchConfirmationEmails = async (event_id: string) => {
  try {
    const queryStringParameters = fetchQueryParams();
    const { response } = await API.get(
      API_NAMES.ADMIN,
      `/events/${event_id}/confirmation-emails`,
      {
        queryStringParameters,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    throw formatError(err);
  }
};

const ADMIN = {
  fetchAlgoliaConfig: admin_fetchAlgoliaConfig,
  fetchEvents: admin_fetchEvents,
  fetchEvent: admin_fetchEvent,
  updateTicketType: admin_updateTicketType,
  updateTicketTypes: admin_updateTicketTypes,
  fetchOrganization: admin_fetchOrganization,
  updateOrganization: admin_updateOrganization,
  createUploadUrl: admin_createUploadUrl,
  createPromoCode: admin_createPromoCode,
  updatePromoCode: admin_updatePromoCode,
  updateQRCode: admin_updateQRCode,
  createAffiliates: admin_createAffiliates,
  updateAffiliate: admin_updateAffiliate,
  createTicketType: admin_createTicketType,
  deleteTicketType: admin_deleteTicketType,
  launchEvent: admin_launchEvent,
  fetchBio: admin_fetchBio,
  updateLink: admin_updateLink,
  createLink: admin_createLink,
  fetchEventMetrics: admin_fetchEventMetrics,
  createEvent: admin_createEvent,
  fetchLinkMetadata: admin_fetchLinkMetadata,
  refreshAccessToken: admin_refreshAccessToken,
  fetchAccessToken: admin_fetchAccessToken,
  fetchMe: admin_fetchMe,
  updateMe: admin_updateMe,
  updateUserScopes: admin_updateUserScopes,
  createUser: admin_createUser,
  deleteUser: admin_deleteUser,
  fetchGuestlist: admin_fetchGuestlist,
  updateGuestlistAllocation: admin_updateGuestlistAllocation,
  deleteGuestlistUser: admin_deleteGuestlistUser,
  createGuestlistUser: admin_createGuestlistUser,
  fetchGuestlistUser: admin_festGuestlistUser,
  searchPerformers: admin_searchPerformers,
  createPerformer: admin_createPerformer,
  deletePerformer: admin_deletePerformer,
  updatePerformers: admin_updatePerformers,
  deleteEvent: admin_deleteEvent,
  cancelEvent: admin_cancelEvent,
  updateBreakeven: admin_updateBreakeven,
  guestlistImportCsv: admin_guestlistImportCsv,
  fetchFileColumns: admin_fetchFileColumns,
  createSystemTicketType: admin_createSystemTicketType,
  updateEventSync: admin_updateEventSync,
  createReportRecipients: admin_createReportRecipients,
  fetchAdsReportRecipients: admin_fetchAdsReportRecipients,
  createAdsReportRecipients: admin_createAdsReportRecipients,
  updateStripeConnection: admin_updateStripeConnection,
  fetchEventBundles: admin_fetchEventBundles,
  fetchEventBundle: admin_fetchEventBundle,
  createAccessCode: admin_createAccessCode,
  updateAccessCode: admin_updateAccessCode,
  linkOrganizationToEvent: admin_linkOrganization,
  disableOrganizationLinkForEvent: admin_disableOrganizationLinkForEvent,
  updateGenres: admin_updateGenres,
  createEventBundle: admin_createEventBundle,
  updateEventBundle: admin_updateEventBundle,
  refundTickets: admin_refundTickets,
  updateAttendeeMetadata: admin_updateAttendeeMetadata,
  updateEventPixels: admin_updateEventPixels,
  duplicateEvent: admin_duplicateEvent,
  updateLinkedOrganization: admin_updateLinkedOrganization,
  fetchOrganizationUsers: admin_fetchOrganizationUsers,
  fillWaitlistOrders: admin_fillWaitlistOrders,
  fetchAttendeeDetail: admin_fetchAttendeeDetail,
  createEventStory: admin_createEventStory,
  updateEventStory: admin_updateEventStory,
  resendReceipt: admin_resendReceipt,
  fetchEventSections: admin_fetchEventSections,
  fetchDashboard: admin_fetchDashboard,
  sendRescheduledCampaign: admin_sendRescheduledCampaign,
  fetchFans: admin_fetchFans,
  fetchCampaignsDashboard: admin_fetchCampaignsDashboard,
  fetchEngageCampaigns: admin_fetchEngageCampaigns,
  fetchEngageAutomations: admin_fetchEngageAutomations,
  fetchEngageAutomation: admin_fetchEngageAutomation,
  createEngageAutomation: admin_createEngageAutomation,
  updateEngageAutomation: admin_updateEngageAutomation,
  createFan: admin_createFan,
  fetchOrganizationTags: admin_fetchOrganizationTags,
  createSegment: admin_createSegment,
  fetchSegments: admin_fetchSegments,
  fetchSegment: admin_fetchSingleSegment,
  exportSegment: admin_exportSegment,
  fetchOrganizationPayouts: admin_fetchOrganizationPayouts,
  fetchSingleOrganizationPayout: admin_fetchSingleOrganizationPayout,
  fetchPayoutDetailsReport: admin_fetchPayoutDetailsReport,
  createTicketSalesReport: admin_createTicketSalesReport,
  fetchTicketSalesReports: admin_fetchTicketSalesReports,
  createScheduledUpdate: admin_createScheduledUpdate,
  updateScheduledUpdate: admin_updateScheduledUpdate,
  deleteScheduledUpdate: admin_deleteScheduledUpdate,
  fetchContact: admin_fetchContact,
  createSegmentQuery: admin_createSegmentQuery,
  createMarketingCampaign: admin_createMarketingCampaign,
  updateMarketingCampaign: admin_updateMarketingCampaign,
  fetchMarketingCampaign: admin_fetchMarketingCampaign,
  updateContact: admin_updateContact,
  contactImportCsv: admin_contactImportCsv,
  duplicateCampaign: admin_duplicateCampaign,
  deleteCampaign: admin_deleteCampaign,
  updateSegment: admin_updateSegment,
  deleteSegment: admin_deleteSegment,
  deleteContact: admin_deleteContact,
  unsubscribeContact: admin_unsubscribeContact,
  launchMarketingCampaign: admin_launchMarketingCampaign,
  duplicateSegment: admin_duplicateSegment,
  fetchEmailTemplates: admin_fetchEmailTemplates,
  fetchEmailTemplate: admin_fetchEmailTemplate,
  createCampaignTest: admin_createCampaignTest,
  fetchContactImportJobs: admin_fetchContactImportJobs,
  fetchContactImportJob: admin_fetchContactImportJob,
  cancelCampaign: admin_cancelCampaign,
  createHold: admin_createHold,
  updateHold: admin_updateHold,
  releaseHold: admin_releaseHold,
  createPublicUploadUrl: admin_createPublicUploadUrl,
  updateBilling: admin_updateBilling,
  createBillingSetupIntent: admin_createBillingSetupIntent,
  fetchInvoices: admin_fetchInvoices,
  calculateFinalTicketPrice: admin_calculateFinalTicketPrice,
  createConfirmationEmail: admin_createConfirmationEmail,
  updateConfirmationEmail: admin_updateConfirmationEmail,
  deleteConfirmationEmail: admin_deleteConfirmationEmail,
  fetchConfirmationEmails: admin_fetchConfirmationEmails,
  updateTimeline: admin_updateTimeline,
  ...ADS_ACTIONS,
};

const ANALYTICS = {
  trackEvents: analytics_trackEvents,
};

const KYD_APIS = {
  ADMIN,
  ANALYTICS,
};

export default KYD_APIS;
